import type { CheckState, Depth } from '@garnet/domain';
import type { PillTone } from '@garnet/ui';
import type { CheckStateVM, DepthVM, HeroCheck, TpmTier } from './types.js';

/**
 * Pure data-shaping for the console — no React, no I/O, no clock (CONVENTIONS).
 * Unit-tested directly in shape.test.ts.
 */

// ── GS1 unit-carrier check digit (PRD §27.2 — claim under test, validated locally) ──
export function gtinCheckDigit(d13: string): number {
  let sum = 0;
  for (let i = 0; i < 13; i++) sum += Number(d13[12 - i]) * (i % 2 === 0 ? 3 : 1);
  return (10 - (sum % 10)) % 10;
}

export interface CarrierState {
  ok: boolean;
  note: string;
}

/** Parse a `(01)<14-digit GTIN>` carrier and validate the trailing check digit locally. */
export function carrierState(v: string): CarrierState {
  const m = (v || '').match(/\((\d{2})\)(\d{14})/);
  if (!m) return { ok: false, note: 'expect (01) + 14-digit GTIN' };
  const body = m[2] ?? '';
  const cd = gtinCheckDigit(body.slice(0, 13));
  return cd === Number(body[13])
    ? { ok: true, note: 'check digit ✓' }
    : { ok: false, note: 'check digit fails · expected ' + cd };
}

// ── country-scaled professional cost (PRD §29.5) ─────────────────────────────
export const COUNTRY: Record<string, string> = {
  GB: 'United Kingdom',
  DE: 'Germany',
  AE: 'United Arab Emirates',
  IN: 'India',
  JO: 'Jordan',
  TR: 'Türkiye',
  NL: 'Netherlands',
  CH: 'Switzerland',
  SG: 'Singapore',
  US: 'United States',
  ES: 'Spain',
  IT: 'Italy',
};
export const COUNTRY_KEYS: string[] = Object.keys(COUNTRY);
const COUNTRY_RATE: Record<string, number> = {
  GB: 1.4, DE: 1.3, CH: 1.8, NL: 1.2, AE: 1.1, JO: 0.8,
  TR: 0.7, IN: 0.6, SG: 1.2, US: 1.5, ES: 1.0, IT: 1.0,
};
export function proCost(base: number, country: string): number {
  return Math.round((base * (COUNTRY_RATE[country] ?? 1)) / 5) * 5;
}

// ── TPM tier suggestion (PRD §31) ────────────────────────────────────────────
export function suggestTier(sum: number): TpmTier {
  return sum >= 13 ? 'critical' : sum >= 10 ? 'high' : sum >= 6 ? 'standard' : 'low';
}

// ── map prototype vocabulary onto @garnet/domain + @garnet/ui ────────────────
const CHECK_STATE: Record<CheckStateVM, CheckState> = {
  machine: 'verified_machine',
  attestation: 'verified_attestation',
  flag: 'flag',
  unverified: 'unverified',
  pending: 'pending',
  error: 'error',
};
export function toCheckState(vm: CheckStateVM): CheckState {
  return CHECK_STATE[vm];
}

const DEPTH: Record<DepthVM, Depth> = {
  type: 'type_lawful',
  identity: 'identity_authentic',
  unit: 'unit_authentic',
  provenance: 'provenance_clean',
};
export function toDepth(vm: DepthVM): Depth {
  return DEPTH[vm];
}

const PILL_TONE: Record<CheckStateVM, PillTone> = {
  machine: 'm',
  attestation: 'a',
  flag: 'f',
  error: 'f',
  unverified: 'n',
  pending: 'n',
};
export function pillTone(vm: CheckStateVM): PillTone {
  return PILL_TONE[vm];
}

const CHECK_LABEL: Record<CheckStateVM, string> = {
  machine: 'verified · machine',
  attestation: 'verified · attestation',
  flag: 'flag',
  error: 'error',
  unverified: 'unverified',
  pending: 'pending',
};
export function checkStateLabel(vm: CheckStateVM): string {
  return CHECK_LABEL[vm];
}

// ── diligence categories (PRD §34 — the six business-facing categories) ──────
export const CATEGORY_ORDER = [
  'Corporate & ownership',
  'Tax & customs',
  'Sector certifications',
  'Sanctions & export control',
  'Insurance',
  'Asset & unit authenticity',
] as const;

const CAT: Record<string, string> = {
  e1: 'Corporate & ownership', e2: 'Corporate & ownership', e3: 'Corporate & ownership',
  t1: 'Tax & customs', t2: 'Tax & customs',
  c1: 'Sector certifications', c3: 'Sector certifications', c4: 'Sector certifications',
  d1: 'Sector certifications', d2: 'Sector certifications', d3: 'Sector certifications',
  s1: 'Sanctions & export control', s2: 'Sanctions & export control',
  i1: 'Insurance',
  c2: 'Asset & unit authenticity', c5: 'Asset & unit authenticity',
  c6: 'Asset & unit authenticity', c7: 'Asset & unit authenticity',
};
export function categoryOf(c: HeroCheck): string {
  return CAT[c.id] ?? 'Other';
}

/** weakest-link selection: flag < unverified/pending < attestation < machine. */
const STATE_RANK: Record<CheckStateVM, number> = {
  flag: 0, error: 0, unverified: 1, pending: 1, attestation: 2, machine: 3,
};
export function categoryState(checks: readonly HeroCheck[]): CheckStateVM {
  return checks.reduce<CheckStateVM>(
    (weak, c) => (STATE_RANK[c.state] < STATE_RANK[weak] ? c.state : weak),
    'machine',
  );
}

export interface AssuranceCounts {
  machine: number;
  attestation: number;
  flag: number;
  unverified: number;
}
export function assuranceCounts(checks: readonly HeroCheck[]): AssuranceCounts {
  const out: AssuranceCounts = { machine: 0, attestation: 0, flag: 0, unverified: 0 };
  for (const c of checks) {
    if (c.state === 'machine') out.machine += 1;
    else if (c.state === 'attestation') out.attestation += 1;
    else if (c.state === 'flag' || c.state === 'error') out.flag += 1;
    else out.unverified += 1;
  }
  return out;
}

// ── depth label expansion (drawer detail) ────────────────────────────────────
export function depthLabel(d: DepthVM): string {
  return {
    type: 'type-lawful',
    identity: 'identity-authentic',
    unit: 'unit-authentic',
    provenance: 'provenance-clean',
  }[d];
}
